/**
 * 3D 构建图 —— 各产品系统的装配模型
 *
 * 每个 builder 返回：
 *   { parts: [Group], rotate: [x,y,z], camera: [x,y,z], target: [x,y,z], fit: number }
 * parts 里每个 Group 的 userData 带有 { name, note, explode }，
 * name/note 会渲染成右侧零件清单与三维标注，explode 是爆炸方向与距离。
 */
import * as THREE from 'three'
import {
  MAT, lathe, pipe, rod, oring, hex, groovedPipe,
  shellSector, couplingHalf, bolt, handwheel, heliCoil, bentTube, part
} from './parts'

const M = (geo, mat) => new THREE.Mesh(geo, mat)
const at = (obj, x, y, z) => { obj.position.set(x, y, z); return obj }

/* ============================================================ 双卡压管件 */
/* 工程不锈钢给水 / 铜管压接系列通用 */
function pressFitting (opt) {
  opt = opt || {}
  const bodyMat = opt.brass ? MAT.brass() : MAT.steel()
  const pipeMat = opt.brass ? MAT.copper() : MAT.steelPipe()

  // 管件本体：两端承口 + 中部密封槽凸台 + 内部止推台阶
  const body = M(lathe([
    [0.44, -1.30], [0.60, -1.30], [0.60, -0.86], [0.68, -0.74], [0.68, -0.50],
    [0.60, -0.38], [0.60, 0.38], [0.68, 0.50], [0.68, 0.74], [0.60, 0.86],
    [0.60, 1.30], [0.505, 1.30], [0.505, 0.22], [0.44, 0.14],
    [0.44, -0.14], [0.505, -0.22], [0.505, -1.30], [0.44, -1.30]
  ]), bodyMat)

  const parts = [
    part('不锈钢 / 铜管材', '管材插入承口至止推台阶，插入深度由内部台阶保证',
      [at(M(pipe(0.5, 0.435, 2.6), pipeMat), 0, -2.3, 0)], [0, -2.4, 0]),
    part('O 型密封圈（下）', 'EPDM 密封圈预装在承口密封槽内，压接后形成径向密封',
      [(() => { const o = M(oring(0.535, 0.062), MAT.rubber(0x14181b)); o.rotation.x = Math.PI / 2; return at(o, 0, -0.62, 0) })()],
      [0, -1.35, 0]),
    part('防漏卡（LBP）', '未压接时留有泄漏通道，试压即可发现漏装，杜绝漏压接头',
      [(() => { const o = M(oring(0.625, 0.03), MAT.rubber(0x1f7a5c)); o.rotation.x = Math.PI / 2; return at(o, 0, -1.18, 0) })()],
      [0, -0.85, 0]),
    part('管件本体', '一体成型承压本体，两端为双卡压承口，中部为密封槽凸台',
      [body], [0, 0, 0]),
    part('O 型密封圈（上）', '同下端密封圈，双端各一，形成两道独立密封',
      [(() => { const o = M(oring(0.535, 0.062), MAT.rubber(0x14181b)); o.rotation.x = Math.PI / 2; return at(o, 0, 0.62, 0) })()],
      [0, 1.35, 0]),
    part('卡压六角痕', '专用压钳一次压接成型，压后呈六角形，为已压接的可视标记',
      [(() => { const m = M(hex(0.7, 0.16), bodyMat); return at(m, 0, 1.18, 0) })()], [0, 0.85, 0]),
    part('不锈钢 / 铜管材', '另一侧管材，管口需去毛刺、倒角后方可插入',
      [at(M(pipe(0.5, 0.435, 2.6), pipeMat), 0, 2.3, 0)], [0, 2.4, 0])
  ]
  return { parts, rotate: [0, 0, Math.PI / 2], camera: [4.2, 2.8, 6.2], fit: 4.2 }
}

/* ========================================================== 沟槽卡箍连接 */
function groovedCoupling () {
  const gasket = M(lathe([
    [0.62, -0.62], [0.80, -0.62], [0.80, -0.40], [0.70, -0.24],
    [0.70, 0.24], [0.80, 0.40], [0.80, 0.62], [0.62, 0.62], [0.62, -0.62]
  ]), MAT.rubber(0x191d20))

  const up = couplingHalf(0.80, 1.06, 1.18, 0.42, 0.86)
  const dn = couplingHalf(0.80, 1.06, 1.18, 0.42, 0.86)
  dn.rotation.y = Math.PI

  const parts = [
    part('沟槽钢管（下）', '管端滚压出标准沟槽，无需焊接与套丝，管口不破坏内防腐层',
      [at(M(groovedPipe(0.62, 0.54, 3.0, 1.18, 0.09, 0.22), MAT.galv()), 0, -2.2, 0)],
      [0, -2.8, 0]),
    part('沟槽钢管（上）', '两根管端对位后即可扣合卡箍，现场装配效率远高于焊接',
      [(() => { const m = M(groovedPipe(0.62, 0.54, 3.0, 1.18, 0.09, 0.22), MAT.galv()); m.rotation.z = Math.PI; return at(m, 0, 2.2, 0) })()],
      [0, 2.8, 0]),
    part('C 型密封胶圈', 'EPDM 三唇密封，管内压力越高唇边贴合越紧（自增强密封）',
      [gasket], [0, 0, 3.4]),
    part('卡箍上壳', '球墨铸铁壳体，两侧凸缘卡入管端沟槽，承受轴向拉脱力',
      [up], [0, 0, 1.9]),
    part('卡箍下壳', '与上壳对开合抱，形成刚性 / 挠性接头',
      [dn], [0, 0, -1.9]),
    part('紧固螺栓组', '两只热浸镀锌螺栓紧固到位即完成连接，可拆卸、可复用',
      [(() => {
        const g = new THREE.Group()
        ;[-1, 1].forEach(s => {
          const b = bolt(0.085, 1.05)
          b.rotation.x = Math.PI / 2
          at(b, s * 0.91, 0, 0)
          g.add(b)
        })
        return g
      })()], [0, -2.6, 0])
  ]
  return { parts, rotate: [0, 0, Math.PI / 2], camera: [4.6, 3.4, 6.4], fit: 4.6 }
}

/* ======================================================== 燃气快装接头 */
function gasQuickJoint () {
  const bodyMat = MAT.brass()
  const body = M(lathe([
    [0.40, -1.05], [0.56, -1.05], [0.56, -0.55], [0.66, -0.44],
    [0.66, 0.44], [0.56, 0.55], [0.56, 1.05], [0.46, 1.05],
    [0.46, 0.30], [0.40, 0.22], [0.40, -1.05]
  ]), bodyMat)

  const collet = M(lathe([
    [0.455, -0.20], [0.55, -0.20], [0.50, 0.20], [0.455, 0.20], [0.455, -0.20]
  ], 96), MAT.steel())

  const parts = [
    part('燃气钢管（下）', '带黄色标识层的燃气专用管材，管端只需切平、去毛刺',
      [at(M(pipe(0.45, 0.39, 2.4), MAT.plastic(0xd9a441)), 0, -2.15, 0)], [0, -2.5, 0]),
    part('锁紧螺母', '拧紧后压缩卡环，机械锁紧管材，全程免动火作业',
      [at(M(hex(0.74, 0.42), bodyMat), 0, -1.22, 0)], [0, -1.6, 0]),
    part('不锈钢卡环', '开口锥形卡环，受压后径向抱紧管壁，提供抗拉脱力',
      [at(collet, 0, -0.82, 0)], [0, -1.0, 0]),
    part('O 型密封圈', 'NBR 耐燃气介质密封圈，负责接头的气密性',
      [(() => { const o = M(oring(0.475, 0.062), MAT.rubber(0x14181b)); o.rotation.x = Math.PI / 2; return at(o, 0, -0.5, 0) })()],
      [0, -0.55, 0]),
    part('接头本体', '全金属承压本体，内设管材止推台阶，插入到位有手感反馈',
      [body], [0, 0, 0]),
    part('燃气钢管（上）', '另一侧管材，插接到位后拧紧螺母即完成连接',
      [at(M(pipe(0.45, 0.39, 2.4), MAT.plastic(0xd9a441)), 0, 2.1, 0)], [0, 2.5, 0])
  ]
  return { parts, rotate: [0, 0, Math.PI / 2], camera: [4.2, 2.9, 6.0], fit: 4.0 }
}

/* ====================================================== 暖通耐腐复合管 */
function multilayerComposite () {
  // 由外到内五层，逐层缩短角度并错开轴向位置，形成剥层剖视
  const S = Math.PI * 0.62 // 剖开口起始角
  const layers = [
    ['外层 PE-RT 保护层', '抗紫外、耐磨损，保护中间铝层不受机械与环境损伤',
      0.92, 0.80, 3.0, Math.PI * 1.62, MAT.plastic(0x2f3d45), 0],
    ['热熔胶粘结层', '把塑料层与铝层牢固粘为一体，杜绝层间剥离',
      0.80, 0.772, 3.5, Math.PI * 1.72, MAT.plastic(0xe4b565), 0],
    ['铝合金中间层', '对接焊铝管，100% 阻氧、定形不回弹、线膨胀系数接近金属管',
      0.772, 0.706, 4.0, Math.PI * 1.80, MAT.alu(), 0],
    ['热熔胶粘结层', '内侧粘结层，保证内衬管与铝层同步受力',
      0.706, 0.678, 4.5, Math.PI * 1.88, MAT.plastic(0xe4b565), 0],
    ['内层 PE-RT 输水层', '卫生级耐高温内衬，内壁光滑、耐腐蚀，长期承受冷热水循环',
      0.678, 0.55, 5.0, Math.PI * 2, MAT.plastic(0xf1f3f4), 0]
  ]
  const parts = layers.map((L, i) => part(L[0], L[1],
    [shellSector(L[2], L[3], L[4], S, L[5], L[6])],
    [0, (layers.length - 1 - i) * -0.95, 0]))

  return { parts, rotate: [0, 0, Math.PI / 2], camera: [4.4, 3.0, 6.0], fit: 4.4 }
}

/* ========================================================== 工程排水三通 */
function drainageTee () {
  const dark = MAT.plastic(0x33383c)
  const body = M(lathe([
    [0.60, -1.10], [0.72, -1.10], [0.72, 0.55], [0.84, 0.70], [0.84, 1.20],
    [0.72, 1.20], [0.72, 0.86], [0.66, 0.74], [0.66, -0.96],
    [0.60, -0.96], [0.60, -1.10]
  ]), dark)

  const branch = M(bentTube([
    [0.1, -0.35, 0], [0.75, 0.25, 0], [1.55, 1.0, 0], [2.25, 1.65, 0]
  ], 0.44), dark)
  const branchBell = M(lathe([
    [0.44, -0.28], [0.56, -0.28], [0.56, 0.28], [0.44, 0.28], [0.44, -0.28]
  ]), dark)
  branchBell.rotation.z = -Math.PI / 4
  branchBell.position.set(2.3, 1.7, 0)

  const parts = [
    part('排水立管（下）', '高密度聚乙烯静音管材，耐酸碱、耐老化，适应复杂排水工况',
      [at(M(pipe(0.60, 0.52, 2.6), dark), 0, -2.4, 0)], [0, -2.6, 0]),
    part('承口密封圈', '唇形橡胶密封圈嵌于承口内，允许管材伸缩位移仍保持密封',
      [(() => { const o = M(oring(0.645, 0.075), MAT.rubber(0x1b1f22)); o.rotation.x = Math.PI / 2; return at(o, 0, 0.92, 0) })()],
      [0, 0, 3.2]),
    part('斜三通本体', '45° 汇流设计，水流平顺、不易返臭与堵塞',
      [body, branch, branchBell], [0, 0, 0]),
    part('排水立管（上）', '上部立管插入承口，插入深度预留伸缩量',
      [at(M(pipe(0.60, 0.52, 2.6), dark), 0, 2.4, 0)], [0, 2.6, 0]),
    part('检查口盖', '带密封垫的可拆检查口，便于日常疏通与检修',
      [(() => {
        const g = new THREE.Group()
        const cap = M(lathe([[0, 0.16], [0.56, 0.16], [0.56, -0.06], [0.46, -0.2], [0, -0.2], [0, 0.16]]), MAT.plastic(0x9aa3a8))
        g.add(cap)
        g.rotation.z = -Math.PI / 4
        return at(g, 2.66, 2.06, 0)
      })()], [1.9, 1.9, 0])
  ]
  return { parts, rotate: [0, 0, 0], camera: [5.2, 3.0, 7.0], fit: 5.0 }
}

/* ====================================================== 市政 PE 电熔套筒 */
function electrofusionCoupler () {
  const peMat = MAT.plastic(0x22262a)
  const sleeve = M(pipe(0.92, 0.66, 2.4), MAT.ghost(0x2b3238))

  const terminals = new THREE.Group()
  ;[-0.62, 0.62].forEach(y => {
    const t = M(lathe([[0.17, 0], [0.17, 0.42], [0.12, 0.5], [0, 0.5], [0, 0], [0.17, 0]]), MAT.plastic(0x4a5259))
    t.rotation.x = Math.PI / 2
    t.position.set(0, y, 0.86)
    terminals.add(t)
  })

  const parts = [
    part('PE 管材（下）', '大口径聚乙烯管材，柔韧抗沉降，可盘卷、可长距离敷设',
      [at(M(pipe(0.65, 0.53, 2.8), peMat), 0, -2.0, 0)], [0, -2.9, 0]),
    part('PE 管材（上）', '管端刮除氧化皮、清洁后插入套筒至中部限位',
      [at(M(pipe(0.65, 0.53, 2.8), peMat), 0, 2.0, 0)], [0, 2.9, 0]),
    part('内嵌电阻丝', '通电后发热熔融管材与套筒内壁，冷却后接口与管材同寿命',
      [(() => { const c = M(heliCoil(0.685, 2.0, 14, 0.028), MAT.wire()); return c })()],
      [0, 0, 3.4]),
    part('电熔套筒', '预埋电阻丝的 PE 套筒，焊接过程由焊机自动控温、可追溯',
      [sleeve], [0, 0, 1.8]),
    part('焊接接线柱', '双接线柱连接焊机，条码标签记录焊接参数',
      [terminals], [0, 0, 2.6])
  ]
  return { parts, rotate: [0, 0, Math.PI / 2], camera: [4.6, 3.2, 6.4], fit: 4.6 }
}

/* ============================================================ 家庭分水器 */
function manifold () {
  const brass = MAT.brass()
  const trunk = M(pipe(0.42, 0.32, 4.4), brass)

  const outlets = new THREE.Group()
  const wheels = new THREE.Group()
  const nuts = new THREE.Group()
  const ys = [-1.5, -0.5, 0.5, 1.5]
  ys.forEach(y => {
    const o = M(rod(0.19, 1.1), brass)
    o.rotation.z = Math.PI / 2
    outlets.add(at(o, 0.78, y, 0))

    const n = M(hex(0.26, 0.22), brass)
    n.rotation.z = Math.PI / 2
    nuts.add(at(n, 1.36, y, 0))

    const stem = M(rod(0.11, 0.7), brass)
    stem.rotation.z = -Math.PI / 2
    const w = handwheel(0.34, 0.08)
    w.rotation.z = Math.PI / 2
    const grp = new THREE.Group()
    grp.add(at(stem, -0.62, y, 0))
    grp.add(at(w, -1.05, y, 0))
    wheels.add(grp)
  })

  const caps = new THREE.Group()
  ;[-1, 1].forEach(s => {
    const c = M(lathe([[0, 0.22], [0.46, 0.22], [0.46, -0.16], [0.30, -0.3], [0, -0.3], [0, 0.22]]), brass)
    c.rotation.z = s > 0 ? 0 : Math.PI
    caps.add(at(c, 0, s * 2.42, 0))
  })

  const parts = [
    part('分水器主体', '一体锻造黄铜主干，进水后均分至各路支管，压力损失小',
      [trunk], [0, 0, 0]),
    part('支管出水口', '每路独立控制，任一路检修不影响其它房间用水',
      [outlets], [1.1, 0, 0]),
    part('管件锁紧螺母', '与热熔 / 卡压管件配套，连接 PP-R、铝塑复合等家装管材',
      [nuts], [2.3, 0, 0]),
    part('分路阀门与手轮', '可单独关断、可调流量，隐蔽工程也能做到可维护',
      [wheels], [-1.9, 0, 0]),
    part('端盖与进水接口', '两端封盖，进水口按现场朝向选择左进或右进',
      [caps], [0, 0, 2.4])
  ]
  return { parts, rotate: [0, 0, 0], camera: [4.6, 3.4, 6.6], fit: 4.6 }
}

/* ============================================================== 球阀 */
function ballValve () {
  const brass = MAT.brass()
  const body = M(lathe([
    [0.40, -1.30], [0.62, -1.30], [0.62, -0.86], [0.86, -0.72],
    [0.86, 0.72], [0.62, 0.86], [0.62, 1.30], [0.40, 1.30], [0.40, -1.30]
  ]), brass)

  const ball = new THREE.Group()
  ball.add(M(new THREE.SphereGeometry(0.66, 48, 32), MAT.steel()))
  const boreMesh = M(new THREE.CylinderGeometry(0.4, 0.4, 1.36, 48), MAT.rubber(0x0e1114))
  ball.add(boreMesh)

  const stem = new THREE.Group()
  stem.add(at(M(rod(0.13, 1.5), MAT.steel()), 0, 0, 0))
  const lever = M(new THREE.BoxGeometry(0.22, 0.16, 1.7), MAT.plastic(0xc0392b))
  stem.add(at(lever, 0, 0.86, 0.72))
  stem.rotation.z = -Math.PI / 2

  const parts = [
    part('进水端接管', '与阀体相连的铜管，端口为标准螺纹 / 压接接口',
      [at(M(pipe(0.4, 0.32, 1.6), MAT.copper()), 0, -2.1, 0)], [0, -2.4, 0]),
    part('PTFE 阀座', '两片聚四氟乙烯阀座抱紧球体，低扭矩启闭、长期不渗漏',
      [(() => {
        const g = new THREE.Group()
        ;[-1, 1].forEach(s => {
          const r = M(lathe([[0.40, -0.09], [0.62, -0.09], [0.62, 0.09], [0.40, 0.09], [0.40, -0.09]]), MAT.plastic(0xf2f2ef))
          g.add(at(r, 0, s * 0.72, 0))
        })
        return g
      })()], [0, 0, 2.6]),
    part('不锈钢球体', '精抛光全通径球体，全开时流道与管径一致，几乎无压损',
      [ball], [0, 0, 1.5]),
    part('阀杆与手柄', '防吹脱阀杆结构，手柄 90° 即完成全开 / 全闭',
      [stem], [2.4, 0, 0]),
    part('阀体与出水端接管', '锻造黄铜阀体，内壁抛光不易结垢，出水侧同样为标准接口',
      [body, at(M(pipe(0.4, 0.32, 1.6), MAT.copper()), 0, 2.1, 0)], [0, 0, 0])
  ]
  return { parts, rotate: [0, 0, Math.PI / 2], camera: [4.4, 3.2, 6.2], fit: 4.4 }
}

/* ------------------------------------------------------------------ 注册 */

const BUILDERS = {
  'press-fitting': () => pressFitting(),
  'press-fitting-copper': () => pressFitting({ brass: true }),
  'grooved-coupling': groovedCoupling,
  'gas-quick-joint': gasQuickJoint,
  'multilayer-composite': multilayerComposite,
  'drainage-tee': drainageTee,
  'electrofusion-coupler': electrofusionCoupler,
  'manifold': manifold,
  'ball-valve': ballValve
}

export function hasModel (key) {
  return !!BUILDERS[key]
}

export function buildModel (key) {
  const fn = BUILDERS[key] || BUILDERS['press-fitting']
  const m = fn()
  m.parts.forEach(p => p.userData.home.copy(p.position))
  return m
}

export default BUILDERS
